import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  QrCode, Package, ScanLine, CheckCircle, ArrowRight, 
  Minus, Plus, Search, AlertTriangle, Box, Warehouse,
  ChevronLeft, ChevronRight, Check, X
} from 'lucide-react';

const QuickPickPage = () => {
  const { revisionId } = useParams();
  const navigate = useNavigate();
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Scan BIN, 2: Select Article, 3: Confirm
  const [bins, setBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binStock, setBinStock] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [pickedItems, setPickedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [manualBinCode, setManualBinCode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (revisionId) {
      fetchRevision();
    }
    fetchBins();
  }, [revisionId]);

  const fetchRevision = async () => {
    try {
      const response = await api.get(`/revisions/${revisionId}`);
      setRevision(response.data);
      // Pre-populate picked items from revision consumables
      if (response.data.consumables_used) {
        setPickedItems(response.data.consumables_used.map(c => ({
          article_id: c.article_id,
          article_name: c.article_name,
          article_code: c.article_code,
          quantity: c.quantity,
          bin_id: c.bin_id
        })));
      }
    } catch (error) {
      toast.error('Eroare la incarcarea reviziei');
    } finally {
      setLoading(false);
    }
  };

  const fetchBins = async () => {
    try {
      const response = await api.get('/bins');
      setBins(response.data);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const fetchBinStock = async (binId) => {
    try {
      const response = await api.get('/stock', { params: { bin_id: binId } });
      setBinStock(response.data.filter(s => s.quantity > 0));
    } catch (error) {
      toast.error('Eroare la incarcarea stocului');
    }
  };

  const handleBinScan = (code) => {
    const bin = bins.find(b => b.code.toLowerCase() === code.toLowerCase());
    if (bin) {
      setSelectedBin(bin);
      fetchBinStock(bin.id);
      setStep(2);
      setShowScanDialog(false);
      setManualBinCode('');
      toast.success(`BIN ${bin.code} selectat`);
    } else {
      toast.error(`BIN ${code} nu a fost gasit`);
    }
  };

  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
    setQuantity(1);
    setStep(3);
  };

  const handleAddToPick = () => {
    if (!selectedArticle || quantity <= 0) return;
    
    if (quantity > Math.round(selectedArticle.quantity)) {
      toast.error(`Stoc insuficient! Disponibil: ${Math.round(selectedArticle.quantity)}`);
      return;
    }

    const existingIndex = pickedItems.findIndex(
      item => item.article_id === selectedArticle.article_id && item.bin_id === selectedBin.id
    );

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...pickedItems];
      updated[existingIndex].quantity += quantity;
      setPickedItems(updated);
    } else {
      // Add new
      setPickedItems([...pickedItems, {
        article_id: selectedArticle.article_id,
        article_name: selectedArticle.article_name,
        article_code: selectedArticle.article_code,
        quantity: quantity,
        bin_id: selectedBin.id,
        bin_code: selectedBin.code
      }]);
    }

    toast.success(`${quantity}x ${selectedArticle.article_name} adaugat`);
    
    // Reset for next pick
    setSelectedArticle(null);
    setQuantity(1);
    setStep(2);
  };

  const handleRemoveItem = (index) => {
    setPickedItems(prev => prev.filter((_, i) => i !== index));
    toast.info('Articol eliminat');
  };

  const handleFinish = async () => {
    if (pickedItems.length === 0) {
      toast.error('Nu ai selectat niciun consumabil');
      return;
    }

    try {
      // Issue stock for each picked item
      for (const item of pickedItems) {
        await api.post('/stock/issue', {
          article_id: item.article_id,
          bin_id: item.bin_id,
          quantity: item.quantity,
          reference_type: 'revision',
          reference_id: revisionId,
          notes: `Quick Pick pentru revizia ${revision?.title || revisionId}`
        });
      }

      // Update revision with consumables used
      if (revisionId) {
        const consumables_used = pickedItems.map(item => ({
          article_id: item.article_id,
          article_name: item.article_name,
          article_code: item.article_code,
          quantity: item.quantity,
          bin_id: item.bin_id
        }));

        await api.put(`/revisions/${revisionId}`, {
          consumables_used: consumables_used
        });
      }

      toast.success('Consumabilele au fost inregistrate!');
      navigate(revisionId ? `/revisions/${revisionId}` : '/wms');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const filteredStock = binStock.filter(s =>
    s.article_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.article_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPicked = pickedItems.reduce((sum, item) => sum + item.quantity, 0);

  if (loading && revisionId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quick-pick-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-[#0052CC]" />
            Quick Pick
          </h1>
          <p className="text-slate-600 mt-1">
            {revision 
              ? `Selecteaza consumabile pentru: ${revision.title}`
              : 'Selecteaza consumabile prin scanare sau manual'}
          </p>
        </div>
        {pickedItems.length > 0 && (
          <Badge className="bg-green-100 text-green-700 text-lg px-4 py-2">
            {totalPicked} articole selectate
          </Badge>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[
          { num: 1, label: 'Selecteaza BIN' },
          { num: 2, label: 'Alege Articol' },
          { num: 3, label: 'Confirma' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div 
              className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors ${
                step === s.num 
                  ? 'bg-[#0052CC] text-white' 
                  : step > s.num 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-500'
              }`}
              onClick={() => {
                if (s.num === 1) {
                  setStep(1);
                  setSelectedBin(null);
                  setSelectedArticle(null);
                } else if (s.num === 2 && selectedBin) {
                  setStep(2);
                  setSelectedArticle(null);
                }
              }}
            >
              {step > s.num ? <CheckCircle className="w-5 h-5" /> : <span className="w-5 h-5 flex items-center justify-center">{s.num}</span>}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < 2 && <ArrowRight className="w-5 h-5 text-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select BIN */}
      {step === 1 && (
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-[#0052CC]" />
              Selecteaza Locatia BIN
            </CardTitle>
            <CardDescription>Scaneaza codul QR al BIN-ului sau selecteaza manual</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button 
                className="btn-primary flex-1 h-14 text-lg"
                onClick={() => setShowScanDialog(true)}
              >
                <ScanLine className="w-6 h-6 mr-2" />
                SCANEAZA BIN
              </Button>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-slate-500">sau selecteaza manual</span>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Cauta BIN..."
                className="pl-10 h-11"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[300px] overflow-y-auto">
              {bins.filter(b => 
                b.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                b.plant_name?.toLowerCase().includes(searchTerm.toLowerCase())
              ).map(bin => (
                <button
                  key={bin.id}
                  onClick={() => handleBinScan(bin.code)}
                  className="p-4 border rounded-lg hover:border-[#0052CC] hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Box className="w-4 h-4 text-slate-400" />
                    <span className="font-mono font-medium">{bin.code}</span>
                  </div>
                  <p className="text-xs text-slate-500">{bin.plant_name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Article */}
      {step === 2 && selectedBin && (
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#0052CC]" />
                  Selecteaza Articol din BIN {selectedBin.code}
                </CardTitle>
                <CardDescription>{binStock.length} articole disponibile in aceasta locatie</CardDescription>
              </div>
              <Button variant="outline" onClick={() => { setStep(1); setSelectedBin(null); }}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Schimba BIN
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Cauta articol..."
                className="pl-10 h-11"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {filteredStock.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredStock.map(stock => (
                  <button
                    key={stock.article_id}
                    onClick={() => handleArticleSelect(stock)}
                    className="w-full p-4 border rounded-lg hover:border-[#0052CC] hover:bg-blue-50 transition-colors text-left flex items-center justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">{stock.article_code}</Badge>
                        <span className="font-medium">{stock.article_name}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">Unitate: {stock.unit || 'buc'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl lg:text-4xl font-bold text-[#0052CC]">{Math.round(stock.quantity)}</p>
                      <p className="text-xs text-slate-500">disponibil</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Nu exista articole in aceasta locatie BIN</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm Quantity */}
      {step === 3 && selectedArticle && (
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Confirma Cantitatea
                </CardTitle>
                <CardDescription>Articol: {selectedArticle.article_name}</CardDescription>
              </div>
              <Button variant="outline" onClick={() => { setStep(2); setSelectedArticle(null); }}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Inapoi
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="font-mono">{selectedArticle.article_code}</Badge>
                <span className="text-slate-500">Stoc disponibil: <strong>{Math.round(selectedArticle.quantity)}</strong></span>
              </div>
              <p className="font-semibold text-lg">{selectedArticle.article_name}</p>
            </div>

            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="lg"
                className="w-14 h-14"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <Minus className="w-6 h-6" />
              </Button>
              <Input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 h-14 text-center text-3xl lg:text-4xl font-bold"
                min={1}
                max={Math.round(selectedArticle.quantity)}
              />
              <Button
                variant="outline"
                size="lg"
                className="w-14 h-14"
                onClick={() => setQuantity(Math.min(Math.round(selectedArticle.quantity), quantity + 1))}
                disabled={quantity >= Math.round(selectedArticle.quantity)}
              >
                <Plus className="w-6 h-6" />
              </Button>
            </div>

            <Button 
              className="w-full h-14 btn-primary text-lg"
              onClick={handleAddToPick}
            >
              <Check className="w-5 h-5 mr-2" />
              ADAUGA {quantity} x {selectedArticle.article_name}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Picked Items Summary */}
      {pickedItems.length > 0 && (
        <Card className="card-industrial border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Package className="w-5 h-5" />
              Articole Selectate ({pickedItems.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              {pickedItems.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">{item.article_code}</Badge>
                      <span className="font-medium">{item.article_name}</span>
                    </div>
                    <p className="text-xs text-slate-500">Din BIN: {item.bin_code}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-lg">{item.quantity}x</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setStep(1)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adauga Mai Multe
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleFinish}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                FINALIZEAZA PICK
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan Dialog */}
      <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Scaneaza sau Introdu Cod BIN
            </DialogTitle>
            <DialogDescription>Introdu codul BIN pentru a continua</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cod BIN</Label>
              <Input
                ref={inputRef}
                value={manualBinCode}
                onChange={(e) => setManualBinCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualBinCode) {
                    handleBinScan(manualBinCode);
                  }
                }}
                placeholder="ex: TEST-RA01-DU01-PO20"
                className="h-12 font-mono"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScanDialog(false)}>Anuleaza</Button>
            <Button 
              className="btn-primary" 
              onClick={() => manualBinCode && handleBinScan(manualBinCode)}
              disabled={!manualBinCode}
            >
              Cauta BIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickPickPage;
